import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  PWA_INSTALL_TIMEOUT_MS,
  hasShownInstallSuccessToast,
  isInAppBrowser,
  isIosDevice,
  isStandaloneDisplayMode,
  markInstallSuccessToastShown,
} from '../lib/pwaInstall';

const PwaInstallContext = createContext(null);

export function PwaInstallProvider({ children }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(() => isStandaloneDisplayMode());
  const [isIos] = useState(() => isIosDevice());
  const [inAppBrowser] = useState(() => isInAppBrowser());
  const [showIosInstallHelp, setShowIosInstallHelp] = useState(false);
  const [showInstallHelp, setShowInstallHelp] = useState(false);
  const [installPhase, setInstallPhase] = useState('idle'); // idle | installing | success | timeout
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  const installTimeoutRef = useRef(null);
  const toastTimeoutRef = useRef(null);

  const clearInstallTimeout = useCallback(() => {
    if (installTimeoutRef.current) {
      window.clearTimeout(installTimeoutRef.current);
      installTimeoutRef.current = null;
    }
  }, []);

  const clearToastTimeout = useCallback(() => {
    if (toastTimeoutRef.current) {
      window.clearTimeout(toastTimeoutRef.current);
      toastTimeoutRef.current = null;
    }
  }, []);

  const showTimedToast = useCallback((message, durationMs = 3200) => {
    clearToastTimeout();
    setToastMessage(message);
    setShowToast(true);
    toastTimeoutRef.current = window.setTimeout(() => {
      setShowToast(false);
      toastTimeoutRef.current = null;
    }, durationMs);
  }, [clearToastTimeout]);

  const completeInstallSuccess = useCallback(() => {
    clearInstallTimeout();
    setDeferredPrompt(null);
    setShowIosInstallHelp(false);
    setShowInstallHelp(false);
    setIsStandalone(true);
    setInstallPhase('success');
    markInstallSuccessToastShown();
    showTimedToast('EchoJham Booking installed. Open it from your home screen.');
    window.setTimeout(() => {
      setInstallPhase((phase) => (phase === 'success' ? 'idle' : phase));
    }, 600);
  }, [clearInstallTimeout, showTimedToast]);

  const completeInstallSuccessRef = useRef(completeInstallSuccess);
  completeInstallSuccessRef.current = completeInstallSuccess;

  useEffect(() => {
    const updateStandaloneMode = () => {
      setIsStandalone(isStandaloneDisplayMode());
    };

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
    };

    const handleAppInstalled = () => {
      completeInstallSuccessRef.current();
    };

    updateStandaloneMode();

    if (isStandaloneDisplayMode() && !hasShownInstallSuccessToast()) {
      markInstallSuccessToastShown();
      showTimedToast('EchoJham Booking installed. Open it from your home screen.');
    }

    const standaloneMedia = window.matchMedia('(display-mode: standalone)');

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    if (typeof standaloneMedia.addEventListener === 'function') {
      standaloneMedia.addEventListener('change', updateStandaloneMode);
    }

    return () => {
      clearInstallTimeout();
      clearToastTimeout();
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      if (typeof standaloneMedia.removeEventListener === 'function') {
        standaloneMedia.removeEventListener('change', updateStandaloneMode);
      }
    };
    // Mount-only listeners; toast helpers are stable enough via refs/closures for first-open toast.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startInstallProgress = useCallback(() => {
    clearInstallTimeout();
    setInstallPhase('installing');
    installTimeoutRef.current = window.setTimeout(() => {
      setInstallPhase('timeout');
      showTimedToast('Still finishing — check your home screen, or try Install again.', 4500);
      installTimeoutRef.current = null;
      window.setTimeout(() => {
        setInstallPhase((phase) => (phase === 'timeout' ? 'idle' : phase));
      }, 800);
    }, PWA_INSTALL_TIMEOUT_MS);
  }, [clearInstallTimeout, showTimedToast]);

  const promptInstall = useCallback(async () => {
    if (isStandalone || installPhase === 'installing') {
      return { outcome: 'already-installed' };
    }

    if (deferredPrompt) {
      const promptEvent = deferredPrompt;
      promptEvent.prompt();

      try {
        const choice = await promptEvent.userChoice;
        if (choice?.outcome === 'accepted') {
          setDeferredPrompt(null);
          startInstallProgress();
          return { outcome: 'accepted' };
        }

        // Prompt can only be used once; keep Install available via help fallback.
        setDeferredPrompt(null);
        return { outcome: 'dismissed' };
      } catch {
        setDeferredPrompt(null);
        setShowInstallHelp(true);
        return { outcome: 'error' };
      }
    }

    if (isIos) {
      setShowIosInstallHelp(true);
      return { outcome: 'ios-help' };
    }

    setShowInstallHelp(true);
    return { outcome: 'manual-help' };
  }, [deferredPrompt, installPhase, isIos, isStandalone, startInstallProgress]);

  const value = useMemo(() => ({
    canShowInstallButton: !isStandalone,
    deferredPromptAvailable: Boolean(deferredPrompt),
    inAppBrowser,
    installPhase,
    isIos,
    isInstalling: installPhase === 'installing',
    isStandalone,
    promptInstall,
    setShowInstallHelp,
    setShowIosInstallHelp,
    showInstallHelp,
    showIosInstallHelp,
    showToast,
    toastMessage,
  }), [
    deferredPrompt,
    inAppBrowser,
    installPhase,
    isIos,
    isStandalone,
    promptInstall,
    showInstallHelp,
    showIosInstallHelp,
    showToast,
    toastMessage,
  ]);

  return (
    <PwaInstallContext.Provider value={value}>
      {children}
    </PwaInstallContext.Provider>
  );
}

export function usePwaInstall() {
  const context = useContext(PwaInstallContext);
  if (!context) {
    throw new Error('usePwaInstall must be used within PwaInstallProvider');
  }
  return context;
}
